import React, { Fragment, useEffect, useState, useContext } from 'react';
import { styled } from '@mui/system';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import {
  Stack,
  Typography,
  Button,
  Popover,
  IconButton,
  Paper,
  FormControl,
  Checkbox,
  Chip,
  Autocomplete,
  TextField,
  TableRow,
  Box,
  TableCell,
  TableBody,
  TableHead,
  TableSortLabel,
  Table,
  TableContainer,
  Tooltip,
} from '@mui/material';
import { FilterList, Close } from '@mui/icons-material';
import { CurrentAppConfig } from 'op-pages/RO/Careplan/AppConfig';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { UserContext } from 'op-contexts';
import { LARGE_DISPLAY_SIZE } from 'shared-components/enums';
import { useWindowSize } from 'shared-components/utils/CustomHooks';

// Initialize dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const ResponsiveContainer = styled('div')`
  overflow-x: auto;
  overflow-y: auto;
  width: 100%;
`;

interface DataProps {
  header: string;
  key: string;
  sort: boolean;
  type: string;
  alignment?: string;
}

interface Props {
  header?: string;
  data: any;
  dataProps: DataProps[];
  isScrollable?: boolean;
  onRowClick?: (rowId: string) => void;
  selectedRow: string | null;
  updateDocumentTypeOptions?: any;
  docTypeOptions?: any;
  emptyStateMessage?: string;
  renderRowIcon?: (row: any) => React.ReactNode;
  renderRowStatus?: (row: any) => React.ReactNode;
  renderRowTooltip?: (row: any) => string | undefined;
  partitionNeedsAttentionFirst?: boolean;
  documentEditModes?: Record<string, boolean>;
}

const ORDERING = {
  ASC: 'asc',
  DESC: 'desc',
};

export const DATATYPE = {
  DATE: 'DATE',
  STRING: 'STRING',
  NUMBER: 'NUMBER',
};

export const documentNeedsAttention = (row: any, documentEditModes: Record<string, boolean> = {}): boolean =>
  row.status?.toLowerCase() === 'draft' ||
  (row.status?.toLowerCase() === 'approved' &&
    (row.id in documentEditModes ? documentEditModes[row.id] : row.hasUncommittedChanges));

/** needs-attention rows, then rest; preserves relative order within each list (no extra sort). */
export const concatActionDocumentsThenRest = (
  documents: any[],
  documentEditModes: Record<string, boolean> = {},
): any[] => {
  const needsAttention = (doc: any) => documentNeedsAttention(doc, documentEditModes);
  return [...documents.filter(needsAttention), ...documents.filter((doc) => !needsAttention(doc))];
};

interface FilterModalProps {
  open: boolean;
  onClose: () => void;
  dataProps: DataProps[];
  onFilterChange: (filters: any) => void;
  anchorEl: HTMLElement | null;
  data: any[];
}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const FilterModal = ({ open, onClose, dataProps, onFilterChange, anchorEl, data }: FilterModalProps) => {
  const [filters, setFilters] = useState<any>({
    title: [],
    statusLabel: [],
  });
  const initialized = React.useRef(false);

  // Natural sort function for more intuitive ordering
  const naturalSort = (a: string, b: string) => {
    return a.toLowerCase().localeCompare(b.toLowerCase());
  };

  // Get unique values for document types and statuses
  const documentTypes = Array.from(new Set(data.map((item) => item.title)))
    .filter(Boolean)
    .sort(naturalSort);
  const statuses = Array.from(new Set(data.map((item) => item.statusLabel)))
    .filter(Boolean)
    .sort(naturalSort);

  // Initialize filters with all document types (except CAS) and all statuses
  useEffect(() => {
    if (
      documentTypes.length > 0 &&
      !initialized.current &&
      (documentTypes.includes('Document(CAS)') || statuses.includes('Void'))
    ) {
      const initialFilters = {
        title: [] as string[],
        statusLabel: [] as string[],
      };
      if (documentTypes.includes('Document(CAS)')) {
        initialFilters.title = documentTypes.filter((title) => title !== 'Document(CAS)');
      }
      if (statuses.includes('Void')) {
        initialFilters.statusLabel = statuses.filter((status) => status !== 'Void');
      }
      setFilters(initialFilters);
      onFilterChange(initialFilters);
      initialized.current = true;
    }
  }, [documentTypes]);

  const handleFilterChange = (key: string, value: any) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}>
      <Paper sx={{ p: 2, width: 350, maxWidth: 350 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">Filter Options</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Stack>
        <Stack spacing={2}>
          <FormControl fullWidth>
            <Autocomplete
              multiple
              options={documentTypes}
              value={filters.title}
              onChange={(_, newValue) => handleFilterChange('title', newValue)}
              renderInput={(params) => {
                const { InputProps, ...other } = params;
                return (
                  <TextField
                    {...other}
                    label="Document Type"
                    variant="standard"
                    InputProps={{
                      ...InputProps,
                      startAdornment: (
                        <>
                          {filters.title.length === 0 && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.secondary',
                                mr: 1,
                              }}>
                              All
                            </Typography>
                          )}
                          {InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                );
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    size="small"
                    {...getTagProps({ index })}
                    deleteIcon={
                      <Close
                        sx={{
                          color: 'white !important',
                          bgcolor: 'secondary.dark',
                          borderRadius: '50%',
                          fontSize: '16px',
                          padding: '2px',
                        }}
                      />
                    }
                  />
                ))
              }
              disableCloseOnSelect
              renderOption={(props, option, { selected }) => {
                const { key, ...optionProps } = props as { key: string };
                return (
                  <Box
                    component="li"
                    key={key}
                    {...optionProps}
                    sx={{
                      paddingBottom: '5px',
                      margin: '0px',
                    }}>
                    <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                    {option}
                  </Box>
                );
              }}
            />
          </FormControl>
          <FormControl fullWidth>
            <Autocomplete
              multiple
              options={statuses}
              value={filters.statusLabel}
              onChange={(_, newValue) => handleFilterChange('statusLabel', newValue)}
              renderInput={(params) => {
                const { InputProps, ...other } = params;
                return (
                  <TextField
                    {...other}
                    label="Status"
                    variant="standard"
                    InputProps={{
                      ...InputProps,
                      startAdornment: (
                        <>
                          {filters.statusLabel.length === 0 && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.secondary',
                                mr: 1,
                              }}>
                              All
                            </Typography>
                          )}
                          {InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                );
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    size="small"
                    {...getTagProps({ index })}
                    deleteIcon={
                      <Close
                        sx={{
                          color: 'white !important',
                          bgcolor: 'secondary.dark',
                          borderRadius: '50%',
                          fontSize: '16px',
                          padding: '2px',
                        }}
                      />
                    }
                  />
                ))
              }
              disableCloseOnSelect
              renderOption={(props, option, { selected }) => {
                const { key, ...optionProps } = props as { key: string };
                return (
                  <Box
                    component="li"
                    key={key}
                    {...optionProps}
                    sx={{
                      paddingBottom: '5px',
                      margin: '0px',
                    }}>
                    <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                    {option}
                  </Box>
                );
              }}
            />
          </FormControl>
        </Stack>
      </Paper>
    </Popover>
  );
};

const SimpleTableFilter = (props: Props): JSX.Element => {
  // Find the date column for initial sorting
  const dateColumn = props.dataProps.find((item) => item.type === DATATYPE.DATE);
  const userDetails = useContext(UserContext);
  const windowSize = useWindowSize();

  const [sortState, setSortState] = useState({
    key: dateColumn?.key || '',
    order: 'desc',
    type: dateColumn?.type || '',
  });
  const [highlightRow, setHighlightRow] = useState<string | null>(null);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [filters, setFilters] = useState<any>({
    title: [],
    statusLabel: [],
  });
  let renderData = props.data;
  const sortKeys = props.dataProps.filter((item) => item['sort']).map((item) => item['key']);

  // Initialize sort state when component mounts
  useEffect(() => {
    if (dateColumn && !sortState.key) {
      setSortState({
        key: dateColumn.key,
        order: 'desc',
        type: dateColumn.type,
      });
    }
  }, [dateColumn, sortState.key]);

  // Apply filters
  if (Object.keys(filters).length > 0) {
    renderData = renderData.filter((row: any) => {
      return Object.entries(filters).every(([key, value]) => {
        if (!value || (Array.isArray(value) && value.length === 0)) {
          return true;
        }
        if (key === 'date') {
          return dayjs(row[key]).format('YYYY-MM-DD') === value;
        }
        if (Array.isArray(value)) {
          return value.includes(row[key]);
        }
        return String(row[key]).toLowerCase().includes(String(value).toLowerCase());
      });
    });
  }

  // Apply sorting after filtering
  if (sortKeys && sortState.key) {
    renderData = [...renderData].sort((a: any, b: any): any => {
      if (sortState.type === DATATYPE.DATE) {
        // Parse ISO date strings (e.g., '2025-07-03T11:34:01+00:00')
        const aDate = dayjs(a[sortState.key]).valueOf();
        const bDate = dayjs(b[sortState.key]).valueOf();

        // Handle invalid dates by putting them at the end
        if (isNaN(aDate) && isNaN(bDate)) return 0;
        if (isNaN(aDate)) return 1;
        if (isNaN(bDate)) return -1;

        return sortState.order === ORDERING.ASC ? aDate - bDate : bDate - aDate;
      } else if (sortState.type === DATATYPE.NUMBER) {
        const aNum = parseFloat(a[sortState.key]) || 0;
        const bNum = parseFloat(b[sortState.key]) || 0;
        return sortState.order === ORDERING.ASC ? aNum - bNum : bNum - aNum;
      } else {
        // String comparison
        const aStr = String(a[sortState.key] || '').toLowerCase();
        const bStr = String(b[sortState.key] || '').toLowerCase();
        return sortState.order === ORDERING.ASC ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
      }
    });
  }

  if (props.partitionNeedsAttentionFirst) {
    const modes = props.documentEditModes ?? {};
    renderData = concatActionDocumentsThenRest(renderData, modes);
  }

  const sortData = (newOrder: string, key?: string, type?: string) => {
    setSortState({
      key: key || sortState.key,
      order: newOrder,
      type: type || sortState.type,
    });
  };

  useEffect(() => {
    setHighlightRow(props.selectedRow);
  }, [props.selectedRow]);

  const attributes = props.dataProps.filter((item) => item.key !== 'title');

  // Reorder columns to put date column second
  const reorderedAttributes = [...attributes].sort((a, b) => {
    if (a.type === DATATYPE.DATE) return 1;
    if (b.type === DATATYPE.DATE) return -1;
    return 0;
  });

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
    setFilterModalOpen(true);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
    setFilterModalOpen(false);
  };

  // Get the correct column order for data cells
  const getDataColumnOrder = () => {
    return ['title', ...reorderedAttributes.map((item) => item.key)];
  };

  const getRowValue = (row: any, colname: string) => {
    if (colname === 'dateOfDocument') {
      return CurrentAppConfig.UseDocumentTimezone
        ? dayjs(row[colname]).tz(userDetails.state.timezone).format(CurrentAppConfig.ShortDateFormat)
        : dayjs(row[colname]).tz('utc').format(CurrentAppConfig.ShortDateFormat);
    }
    return row[colname];
  };

  const needsAttentionRestBoundaryIndex = (() => {
    if (!props.partitionNeedsAttentionFirst || renderData.length === 0) return null;
    const modes = props.documentEditModes ?? {};
    const firstRestIndex = renderData.findIndex((row: any) => !documentNeedsAttention(row, modes));
    return firstRestIndex > 0 ? firstRestIndex - 1 : null;
  })();

  const columnCount = getDataColumnOrder().length;

  return (
    <ResponsiveContainer style={{ minHeight: '100%' }}>
      {renderData && renderData.length > 0 ? (
        <TableContainer sx={{ height: '100%' }}>
          <Table data-test-id={`${props.header}-table`}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'white', position: 'sticky', top: 0, zIndex: 10 }}>
                {/* Filter button */}
                <TableCell sx={{ paddingLeft: '3px !important' }}>
                  <Button
                    data-test-id={`${props.header}-filter-button`}
                    endIcon={<FilterList />}
                    onClick={handleFilterClick}
                    variant="text"
                    size="small"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: 'action.active',
                      '& .MuiButton-endIcon': {
                        marginLeft: 0.5,
                      },
                    }}>
                    <Typography sx={{ fontFamily: 'Poppins', fontWeight: 500, lineHeight: '24px' }}>Filter</Typography>
                    {(filters.title.length > 0 || filters.statusLabel.length > 0) && (
                      <Typography
                        sx={{
                          bgcolor: 'secondary.light',
                          color: 'primary.main',
                          borderRadius: '50%',
                          minWidth: 20,
                          height: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          px: 0.5,
                        }}>
                        {filters.title.length + filters.statusLabel.length}
                      </Typography>
                    )}
                  </Button>
                </TableCell>
                {/* Date column (sortable) */}
                {reorderedAttributes.map(
                  (item: DataProps, index: number): JSX.Element => (
                    <TableCell
                      key={`head-${index}`}
                      sx={{
                        padding: 1,
                        textAlign: 'right',
                        paddingRight: '16px',
                      }}>
                      {sortKeys && sortKeys.includes(item['key']) ? (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                          }}>
                          <TableSortLabel
                            active={sortState.key === item['key']}
                            direction={sortState.order as 'asc' | 'desc'}
                            onClick={() => {
                              const newOrder =
                                sortState.key === item['key'] && sortState.order === ORDERING.ASC
                                  ? ORDERING.DESC
                                  : ORDERING.ASC;
                              sortData(newOrder, item['key'], item['type']);
                            }}
                            sx={{
                              '& .MuiTableSortLabel-icon': {
                                color: 'action.active',
                                opacity: 1,
                              },
                              '&.Mui-active .MuiTableSortLabel-icon': {
                                color: 'action.active',
                              },
                              flexDirection: 'row-reverse',
                            }}>
                            <Typography sx={{ fontWeight: 500, lineHeight: '24px' }}>{item['header']}</Typography>
                          </TableSortLabel>
                        </Box>
                      ) : (
                        <Typography sx={{ fontWeight: 500, lineHeight: '24px' }}>{item['header']}</Typography>
                      )}
                    </TableCell>
                  ),
                )}
              </TableRow>
            </TableHead>
            <TableBody className="document-list-table">
              {renderData.map((row: any, rowIndex: number) => (
                <Fragment key={`row-${row.id}`}>
                  <Tooltip
                    title={props.renderRowTooltip ? props.renderRowTooltip(row) ?? '' : ''}
                    placement="left"
                    arrow>
                    <TableRow
                      onClick={(): void => {
                        setHighlightRow(row.id);
                        props.onRowClick && props.onRowClick(row.id);
                      }}
                      sx={{
                        bgcolor: (theme) => (highlightRow === row.id ? 'action.selected' : 'transparent'),
                        '& td': {
                          textAlign: 'left',
                          borderBottom: 'none',
                        },
                        '& td:last-of-type': {
                          textAlign: 'right',
                        },
                        '&:hover': {
                          bgcolor: (theme) => (highlightRow === row.id ? 'action.selected' : 'action.hover'),
                        },
                      }}>
                      {getDataColumnOrder().map((colname, colIndex) => (
                        <TableCell
                          key={`col-${colIndex}`}
                          data-test-id={colIndex === 0 ? `docs-table-row-${row['title']}` : undefined}
                          sx={{
                            minHeight: 44,
                            padding: 1,
                          }}>
                          {colIndex === 0 ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                              {props.renderRowIcon && props.renderRowIcon(row)}
                              <Stack spacing={0.5}>
                                <Typography variant="body2">{row[colname]}</Typography>
                                <Stack spacing={0.25} alignItems="flex-start">
                                  <Typography variant="caption" color="text.secondary">
                                    {row.statusLabel === 'Draft' ? '-' : row.statusLabel || row.status}
                                  </Typography>
                                  {props.renderRowStatus ? props.renderRowStatus(row) : null}
                                </Stack>
                              </Stack>
                            </Stack>
                          ) : (
                            getRowValue(row, colname)
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </Tooltip>
                  {needsAttentionRestBoundaryIndex !== null && rowIndex === needsAttentionRestBoundaryIndex && (
                    <TableRow role="presentation" sx={{ pointerEvents: 'none', '&:hover': { bgcolor: 'transparent' } }}>
                      <TableCell colSpan={columnCount} sx={{ border: 'none', p: 0, bgcolor: 'background.paper' }}>
                        {/* Paper gap above the line (needs-attention side) */}
                        <Box sx={(theme) => ({ height: theme.spacing(1) })} aria-hidden />
                        {/* Grey divider line + paper gap below it (non-attention side) */}
                        <Box
                          sx={(theme) => ({
                            borderTop: `1px solid ${theme.palette.grey[300]}`,
                            height: theme.spacing(1),
                          })}
                          aria-hidden
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : props.emptyStateMessage ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          {props.emptyStateMessage}
        </Typography>
      ) : null}
      <FilterModal
        open={filterModalOpen}
        onClose={handleFilterClose}
        dataProps={props.dataProps}
        onFilterChange={setFilters}
        anchorEl={filterAnchorEl}
        data={props.data}
      />
    </ResponsiveContainer>
  );
};

export default SimpleTableFilter;
